import { formatMm } from '@components/TireOffer/TireOfferLanding/formatMm';
import { t } from '@lingui/core/macro';
import type { CarTireTreadDepthLatestResponseRecord } from '@navio/nsdk';
import { TireSeason } from '@navio/nsdk';
import { NoddiCard, NoddiLink, NoddiSegmentedControl } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { NavioTireSeasonChip, TreadDepthMeter, TreadDepthMeterLegend } from 'noddi-web';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { CarTireSeason, getSeasonTreadDepth } from './seasonTreadDepth';
import { WheelSetStatusBadge } from './WheelSetStatusBadge';

type Props = {
  to: string;
  latestTreadDepth: CarTireTreadDepthLatestResponseRecord | undefined;
  /** Owned by the page, so a season tile in the hero can open the card on its season. */
  season: CarTireSeason;
  onSeasonChange: (season: CarTireSeason) => void;
};

export const TireDetails = ({ to, latestTreadDepth, season, onSeasonChange }: Props) => {
  const translations = getCommonTranslations().wheelMeasurements;
  const { status, wheels, thresholds, measuredAt } = getSeasonTreadDepth(latestTreadDepth, season);
  const seasonLabel = season === TireSeason.SUMMER ? translations.summerTires : translations.winterTires;

  return (
    <NoddiCard
      title={translations.tires}
      headerNode={
        <NoddiSegmentedControl<CarTireSeason>
          ariaLabel={t`Tire season`}
          value={season}
          onValueChange={onSeasonChange}
          options={[
            { value: TireSeason.SUMMER, label: translations.summerTires },
            { value: TireSeason.WINTER, label: translations.winterTires }
          ]}
        />
      }
      contentClassName='flex flex-col gap-6'
    >
      <div className='flex items-center gap-4'>
        <NavioTireSeasonChip season={season} iconOnly size='lg' translations={translations} />
        <div className='flex min-w-0 flex-col gap-1'>
          <span className='text-lg font-semibold'>{seasonLabel}</span>
          <WheelSetStatusBadge status={status} />
        </div>
      </div>

      {wheels.length > 0 && (
        <div className='flex flex-col gap-4'>
          {wheels.map(({ label, mm }) => (
            <TreadDepthMeter key={label} label={label} valueMm={mm} thresholds={thresholds} formatNumber={formatMm} />
          ))}
          <div className='flex flex-wrap justify-between gap-2'>
            {thresholds && (
              <TreadDepthMeterLegend thresholds={thresholds} translations={translations} formatNumber={formatMm} />
            )}
            {measuredAt && (
              <span className='text-xs text-muted-foreground'>
                {translations.measuredAt} {noddiFormatDate(measuredAt, NoddiDateFormats.DATE)}
              </span>
            )}
          </div>
          <NoddiLink to={to} variant='link' endIcon='ArrowRight' className='self-end px-0'>
            {translations.seeMeasurements}
          </NoddiLink>
        </div>
      )}
    </NoddiCard>
  );
};
